import { useCallback } from 'react';
import { NOTIFY_TYPE, STALE_RECORD_MESSAGE } from '../../../../../util/constants';
import { isStaleRecordError } from '../../../../../util/optimisticLock';
import { useMutation } from '@apollo/react-hooks';
import RESUBMIT_ORDER_DETAILS from '../../../../../graphql/mutations/resubmitOrderDetails';
import CREATE_UPDATE_INVENTORY_RETURN_LINE_ITEMS from '../../../../../graphql/mutations/createUpdateInventoryReturnLineItems';
import { useSavePartsProps } from './UseSavePartsProps';

const useSaveParts = ({
    setIsSaving,
    inventoryReturnDetail,
    isRepstockAccount,
    inventoryPartsToAdd,
    catalogPartsToAdd,
    returnType,
    handleClose,
    openSnackBar,
}: useSavePartsProps) => {
    const [reSubmitInventory] = useMutation(RESUBMIT_ORDER_DETAILS);
    const [createUpdateInventoryReturn, { data: createUpdateInventoryReturnMutation, loading: loadingLineItems }] = useMutation(
        CREATE_UPDATE_INVENTORY_RETURN_LINE_ITEMS
    );

    const onAddClick = useCallback(async () => {
        setIsSaving(true);
        const mutation: any = {
            orderId: inventoryReturnDetail.orderId,
            orderExternalId: inventoryReturnDetail.orderExternalId,
            salesRepId: inventoryReturnDetail.salesRepId,
            formType: 'New',
        };
        const partSourceLocation = isRepstockAccount ? 'Repstock' : 'Customer';
        const lineItems: any[] = [];

        if (inventoryPartsToAdd.length > 0) {
            inventoryPartsToAdd.forEach((lotInfo: any) => {
                if (lotInfo.counterValue) {
                    const obj: any = {
                        lotNumber: lotInfo.lotNumber,
                        productId: lotInfo.productId,
                        quantity: lotInfo.counterValue.toString(),
                        productNumber: lotInfo.productNumber,
                        isLotNumber: lotInfo?.isLotNumber,
                        subInventoryCode: lotInfo?.subInventoryCode || '',
                        partAddedFrom: lotInfo?.sourceLocation,
                        sourceLocation:
                            lotInfo?.sourceLocation !== 'Catalog' ? lotInfo?.sourceLocation : partSourceLocation,
                    };
                    inventoryReturnDetail.lineItems.forEach((item: any) => {
                        if (item.productId === lotInfo.productId && item.lotNumber === lotInfo.lotNumber) {
                            obj.externalId = item.externalId;
                        }
                    });
                    lineItems.push(obj);
                }
            });
        }
        if (catalogPartsToAdd.length > 0) {
            catalogPartsToAdd.forEach((lotInfo: any): void => {
                if (lotInfo.changed) {
                    const obj: any = {
                        productNumber: lotInfo.productNumber,
                        productId: lotInfo.productId,
                        quantity: lotInfo.counterValue.toString(),
                        isLotNumber: lotInfo?.isLotNumber,
                        subInventoryCode: lotInfo?.subInventoryCode || '',
                        partAddedFrom: lotInfo?.sourceLocation,
                        sourceLocation:
                            lotInfo?.sourceLocation !== 'Catalog' ? lotInfo?.sourceLocation : partSourceLocation,
                    };
                    lineItems.push(obj);
                }
            });
        }
        mutation.lineItems = lineItems;
        if (returnType === 'Failed') {
            const variables = {
                id: inventoryReturnDetail.id,
                caseId: inventoryReturnDetail.caseId,
                caseExternalId: inventoryReturnDetail.caseExternalId,
                submitType: 'Update',
                lineItems,
                salesRepId: inventoryReturnDetail.salesRepId,
                expectedCaseVersion: inventoryReturnDetail.caseVersion,
            };
            reSubmitInventory({
                variables,
            }).then((res: any) => {
                if (res && res.data && res.data.resubmitOrderDetails?.message === NOTIFY_TYPE.SUCCESS) {
                    handleClose();
                }
            })
                .catch((error: any) => {
                    if (isStaleRecordError(error)) {
                        openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
                    } else if (error && error.message) {
                        openSnackBar(NOTIFY_TYPE.ERROR, `${error.message}`);
                    } else openSnackBar(NOTIFY_TYPE.ERROR, 'Error: Inventory Return error.');
                    // eslint-disable-next-line no-console
                    console.log('We had a trouble saving data to salesforce. Please try again later');
                    // eslint-disable-next-line no-console
                    console.log(error);
                })
                .finally(() => {
                    setIsSaving(false);
                });
        } else {
            createUpdateInventoryReturn({
                variables: {
                    ...mutation,
                    expectedCaseVersion: inventoryReturnDetail?.caseVersion,
                },
            })
                .then((res: any) => {
                    if (res?.data?.createUpdateInventoryReturnLineItems?.message === 'success') {
                        handleClose();
                    }
                })
                .catch((error: any) => {
                    /* Keep the modal open so a rejected add does not look like it went through. */
                    if (isStaleRecordError(error)) {
                        openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
                        return;
                    }
                    if (error && error.message) {
                        openSnackBar(NOTIFY_TYPE.ERROR, `${error.message}`);
                    } else openSnackBar(NOTIFY_TYPE.ERROR, 'Error: Inventory Return error.');
                    // eslint-disable-next-line no-console
                    console.log('We had a trouble saving data to salesforce. Please try again later');
                    // eslint-disable-next-line no-console
                    console.log(error);
                })
                .finally(() => {
                    setIsSaving(false);
                });
        }
    }, [
        setIsSaving,
        inventoryReturnDetail,
        isRepstockAccount,
        inventoryPartsToAdd,
        catalogPartsToAdd,
        returnType,
        reSubmitInventory,
        createUpdateInventoryReturn,
        handleClose,
        openSnackBar,
    ]);

    return { onAddClick, loadingLineItems };
};

export default useSaveParts;